import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig, isMultiStudyConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { ContactForm } from "../components/ContactForm";

import { useQuestionnaireAnswersState } from "../components/contact-form/utils/questionnaireStorage";
import { useScrollToTop } from "../hooks/useScrollManagement";
import {
  mergePartnerConfig,
  usePartnerConfig,
} from "../hooks/usePartnerConfig";

/**
 * Contact page component - renders the contact form step
 * Designed to be rendered within the LandingPageFactory system
 */
export function ContactPageContent({ config }: { config: LandingPageConfig }) {
  const [questionnaireResults] = useQuestionnaireAnswersState();
  const partnerConfig = usePartnerConfig();
  useScrollToTop();

  // For multi-study pages, use the matched study's trial ID from questionnaire results
  // For regular pages, use the static trial ID from config
  const trialIdToUse = isMultiStudyConfig(config)
    ? questionnaireResults?.matchedStudyTrialId
    : config.trialIdToAutoApply;


  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="contact-page">
        {/* Background orbs container - only visible on larger screens */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          {/* Large icon */}
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          {/* Medium icon */}
          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          {/* Small pulsing icon */}
          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        {/* Main content container */}
        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-16 pb-12 md:pb-24"
          data-testid="contact-content"
        >
          <div className="text-center mb-4 md:mb-12">
            <h1
              className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl px-2 sm:px-4"
              data-testid="contact-title"
            >
              <span className="block mb-3 md:mb-4">{config.heroTitle}</span>
              {config.heroSubtitle && (
                <span className="block text-[1.375rem] md:text-[2rem] lg:text-[2.75rem] bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] md:leading-[1.3] overflow-visible">
                  {config.heroSubtitle}
                </span>
              )}
            </h1>
          </div>

          {/* Contact form container */}
          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <div
              className="w-full max-w-4xl mx-auto px-2"
              data-testid="contact-form-container"
            >
              <ContactForm
                questionnaireAnswers={questionnaireResults}
                landingPageId={config.id}
                countryCode={config.countryCode}
                trialIdConfig={trialIdToUse}
                submitButtonText={config.submitButtonText}
                requireEmrConsent={config.requireEmrConsent}
              />
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
